import { createFileRoute, notFound } from "@tanstack/react-router";
import { breadcrumbSchema, datasetSchema } from "@/lib/schema";
import { faqSchema } from "@/components/PageFaq";
import { TopicArticle } from "@/components/pages/TopicArticle";
import { findTopic } from "@/lib/topic-pages";
import { NotFoundView } from "@/components/RouteFallbacks";

const BASE = "https://palstars.com/palworld-wiki";

export const Route = createFileRoute("/palworld-wiki/$topic")({
  // The wiki corpus is ~57 KB of prose. Imported at module scope it landed in
  // the shared entry chunk, so every visitor downloaded every wiki article.
  // Loading it inside the loader keeps it in its own chunk, fetched only when
  // someone opens a wiki topic, and the component reads it from loader data.
  loader: async ({ params }) => {
    const { WIKI_PAGES } = await import("@/lib/wiki-pages");
    const page = findTopic(WIKI_PAGES, params.topic);
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData, params }) => {
    const page = loaderData?.page;
    const url = `${BASE}/${params.topic}`;
    if (!page) {
      return {
        meta: [
          { title: "Palworld Wiki Article Not Found | Pal Stars" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    return {
      meta: [
        { title: page.title },
        { name: "description", content: page.description },
        { property: "og:title", content: page.h1 },
        { property: "og:description", content: page.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: page.h1 },
        { name: "twitter:description", content: page.description },
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbSchema([
          ["Home", "/"],
          ["Palworld Wiki", "/palworld-wiki"],
          [page.h1, `/palworld-wiki/${page.slug}`],
        ]),
        faqSchema(page.faq, url),
        ...(page.table
          ? [
              datasetSchema({
                name: page.table.caption,
                description: page.description,
                path: `/palworld-wiki/${page.slug}`,
                keywords: page.table.keywords,
                variables: page.table.columns,
              }),
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${url}#article`,
            url,
            headline: page.h1,
            description: page.description,
            about: { "@type": "VideoGame", name: "Palworld" },
            articleSection: page.sections.map((s) => s.heading),
            isPartOf: { "@type": "WebSite", name: "Pal Stars", url: "https://palstars.com" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SpeakableSpecification",
            cssSelector: [".aeo-answer"],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <NotFoundView title="Wiki article not found" message="That Palworld wiki article does not exist. Browse the wiki index instead." />
  ),
  component: WikiTopicPage,
});

function WikiTopicPage() {
  const { page } = Route.useLoaderData();
  return (
    <TopicArticle
      page={page}
      eyebrow="Palworld Wiki"
      parent={{ to: "/palworld-wiki", label: "Palworld Wiki" }}
      faqHeading={`${page.h1} FAQ`}
    />
  );
}
